import { useMemo, useSyncExternalStore } from 'react';
import { LANGS, TRANSLATIONS, type Lang } from './translations';

const STORAGE_KEY = 'draw:lang';

function pickDefault(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
    if (saved && saved in TRANSLATIONS) return saved;
  } catch {
    /* noop */
  }
  return 'he';
}

let currentLang: Lang = pickDefault();
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getLang(): Lang {
  return currentLang;
}

export function setLang(lang: Lang): void {
  if (lang === currentLang) return;
  currentLang = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* noop */
  }
  applyHtmlAttrs();
  listeners.forEach((l) => l());
}

export function applyHtmlAttrs(): void {
  const lang = currentLang;
  const dir = LANGS.find((l) => l.code === lang)?.dir ?? 'ltr';
  document.documentElement.setAttribute('lang', lang);
  document.documentElement.setAttribute('dir', dir);
  const title = TRANSLATIONS[lang]['app.title'];
  if (title) document.title = title;
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, getLang);
}

export function useT(): (key: string, fallback?: string) => string {
  const lang = useLang();
  // useMemo критичен: без него каждый рендер возвращал бы новую функцию,
  // и любой useEffect с t в deps крутил бы бесконечный цикл.
  return useMemo(() => {
    const dict = TRANSLATIONS[lang];
    return (key: string, fallback?: string) => dict[key] ?? fallback ?? key;
  }, [lang]);
}

export { LANGS };
export type { Lang };
